/**
 * created by hezq on: 2022-10-10
 *
 * description： 插件核心逻辑
 *
 */

import Cookies from 'js-cookie'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import util from './util.js'
import ajax from './ajax.js'
import api from './api.js'
import { addHandler } from './event.js'

const isMobile = util.isMobileDevice()
let pageViewUploading = false
let pageViewWaiting = []
let pageEventUploading = false
let pageEventWaiting = []
let isVueEnv = false

const fpPromise = FingerprintJS.load()

/**
 * description: 生成指纹id
 * @return {String}
 *
 * created by hezq on 2022-11-09 15:30:16
 *
 */
const getCid = async function getCid() {
	try {
		const fp = await fpPromise
		const result = await fp.get() || {}
		return result.visitorId || ''
	} catch (e) {
		const rcid = util.getLS('rcid')
		if (!rcid) {
			const cid = util.GUID()
			util.setLS('rcid', cid)
			return cid
		}
		return rcid
	}
	return ''
}

/**
 * description: 获取tid
 *
 * created by hezq on 2022-12-02 09:20:35
 *
 */
function getTid() {
	// 链路id
	let tidValue = ''
	const tid = util.getSS('tid')
	if (!tid) {
		tidValue = util.GUID()
		util.setSS('tid', util.GUID())
	} else {
		tidValue = tid
	}
	return tidValue
}

/**
 * description: 获取	：是否当日首次访问、上一页地址、链路id
 * @return {String} 
 *
 * created by hezq on 2022-11-09 17:21:54
 *
 */
function getFirstVisit() {
	// 是否当日首次访问 1-首次 0-非首次
	let fvValue = 0
	const fv = util.getLS('fv')
	const currentDate = util.dateFormat(new Date(), 'yyyy-MM-dd')
	if (!fv || fv && fv !== currentDate) {
		// 首次访问
		util.setLS('fv', currentDate)
		fvValue = 1
	}

	return fvValue
}

/**
 * description: 获取vue的全路径
 * @return {String} 
 *
 * created by hezq on 2022-12-02 09:46:56
 *
 */
function getVueFullPath(appName, path) {
	let fullPath = ''
	if (appName) {
		fullPath = util.getLocationOrigin() + '/' + appName
		if (path) {
			fullPath += path
		}
	}
	return fullPath
}

/**
 * description: 记录脚本错误/警告信息
 * @param {String} type 类型 error/warn
 * @param {String} message 信息
 *
 * created by hezq on 2022-10-10
 *
 */
const pushScriptInfo = async function pushScriptInfo(type, message) {
	const cid = await getCid()
	const param = {
		url: window.location.href,
		name: document.title || '',
		ename: type === 'error' ? '脚本报错[Script Error]' : '脚本警告[Script Warn]',
		cid: cid,
		did: window._behaviourOptions.deviceId || '',
		mf: window._behaviourOptions.manufacturer || '',
		md: window._behaviourOptions.model || '',
		time: util.dateFormat(new Date()),
		source: isMobile ? 1 : 0,
		extend: message,
		sw: window.screen.width,
		sh: window.screen.height,
		zid: cid,
		tid: getTid()
	}
	eventUploading([param])
}

/**
 * description: 记录页面url，对外暴露
 * @param {String} appName 项目名称
 * @param {String} path 浏览器地址
 *
 * created by hezq on 2022-10-10
 *
 */
const pushPageView = async function pushPageView(appName, fromPath, toPath) {
	// 处理地址
	let url = ''
	let rf = ''
	const referrer = document.referrer || ''
	
	if (isVueEnv) {
		if (appName) {
			url = getVueFullPath(appName, toPath)
			rf = getVueFullPath(appName, fromPath)
		} else {
			url = window.location.href
			rf = referrer === url ? '' : referrer
		}
	} else {
		url = window.location.href
		rf = referrer === url ? '' : referrer
	}

	const cid = await getCid()
	const param = {
		fv: getFirstVisit(),
		rf: rf,
		url: url,
		name: document.title || '',
		source: isMobile ? 1 : 0,
		time: util.dateFormat(new Date()),
		did: window._behaviourOptions.deviceId || '',
		cid: cid,
		mf: window._behaviourOptions.manufacturer || '',
		extend: window._behaviourOptions.extend || '',
		md: window._behaviourOptions.model || '',
		sw: window.screen.width,
		sh: window.screen.height,
		zid: cid
	}

	const key = 'pageBehaviourCache'
	const waitingKey = 'pageBehaviourWaitingCache'
	const pageBehaviourWaitingCache = util.getLS(waitingKey, true)

	if (pageViewUploading) {
		// 如果上一次还在上传，先把数据缓存起来
		if (pageBehaviourWaitingCache) {
			pageViewWaiting = [...pageBehaviourWaitingCache, param]
		} else {
			pageViewWaiting = [param]
		}
		util.setLS(waitingKey, pageViewWaiting)
		pageViewWaiting = []
	} else {
		let queue = []
		let pageBehaviourCache = util.getLS(key, true)
		if (pageBehaviourWaitingCache) {
			if (pageBehaviourCache) {
				util.setLS(key, [...pageBehaviourWaitingCache, ...pageBehaviourCache])
			} else {
				util.setLS(key, pageBehaviourWaitingCache)
			}
			util.setLS(waitingKey, [])
		}

		pageBehaviourCache = util.getLS(key, true)

		// 合并数据
		if (!pageBehaviourCache) {
			queue = [param]
		} else {
			queue = [...pageBehaviourCache, param]
		}

		if (queue.length > window._behaviourOptions.pageInterval) {
			pageViewUploading = true
			// 暂时把此时的全部数据都上传
			viewUploading(queue, function() {
				util.setLS(key, [])
				pageViewUploading = false
			})
		} else {
			util.setLS(key, queue)
		}
	}
}

/**
 * description: 页面访问记录上传
 * @param {Array} queue 待上报数据
 * @param {Function} cb 上传成功之后回调
 *
 * created by hezq on 2022-10-10
 *
 */
function viewUploading(queue, cb) {
	ajax({
		url: api.reportPageView,
		method: 'post',
		data: {
			data: queue
		},
		success: () => {
			cb && cb()
		},
		error: () => {

		}
	})
}

/**
 * description: 记录页面点击事件，对外暴露
 * @param {String} path 浏览器地址
 *
 * created by hezq on 2022-10-10
 *
 */
const pushClickEvent = async function pushClickEvent(path, btnName, extend) {
	if (!window._behaviourOptions.handleClickEvent) {
		return
	}

	const key = 'eventBehaviourCache'
	const cid = await getCid()
	const param = {
		url: path || window.location.href,
		name: document.title || '',
		ename: btnName || '',
		cid: cid,
		did: window._behaviourOptions.deviceId || '',
		mf: window._behaviourOptions.manufacturer || '',
		md: window._behaviourOptions.model || '',
		time: util.dateFormat(new Date()),
		source: isMobile ? 1 : 0,
		extend: extend || '',
		sw: window.screen.width,
		sh: window.screen.height,
		zid: cid,
		tid: getTid()
	}

	if (pageEventUploading) {
		// 如果正在上传，先把数据存入缓冲队列
		pageEventWaiting.push(param)
	} else {
		let queue = []
		const eventBehaviourCache = util.getLS(key, true)
		// 合并数据
		if (!eventBehaviourCache) {
			queue = [...pageEventWaiting, param]
		} else {
			queue = [...eventBehaviourCache, ...pageEventWaiting, param]
		}

		if (queue.length > window._behaviourOptions.eventInterval) {
			pageEventUploading = true
			// 暂时把此时的全部数据都上传
			eventUploading(queue, function() {
				util.setLS(key, [])
				pageEventUploading = false
			})
		} else {
			util.setLS(key, queue)
		}
	}
}

/**
 * description: 页面事件记录上传
 * @param {Array} queue 待上报数据
 * @param {Function} cb 上传成功之后回调
 *
 * created by hezq on 2022-10-10
 *
 */
function eventUploading(queue, cb) {
	ajax({
		url: api.reportEvent,
		method: 'post',
		data: {
			data: queue
		},
		success: () => {
			cb && cb()
		},
		error: () => {

		}
	})
}

/**
 * description: 插件初始化
 * @param {Object} options 配置项 
 * @param {Vue} VM Vue对象
 *
 * created by hezq on 2022-10-10
 *
 */
function init(options, VM) {
	// 校验必要参数
	if (!options) {
		console.error('arguments is required')
		return
	}

	if (!options.appKey) {
		// appKey 客户端标识
		console.error('appKey is required in arguments')
		return
	}

	if (!options.server) {
		// 接口服务域名
		console.error('server is required in arguments')
		return
	}

	// 全局参数挂载
	window._behaviourOptions = options

	if (!window._behaviourOptions.pageInterval) {
		// 几条数据上报一次，默认实时上传
		window._behaviourOptions.pageInterval = 0
	}

	if (VM) {
		isVueEnv = true
	}

	// window全局事件监听
	addHandler(window, 'load', function(e) {
		pushPageView()
	})

	if (!window._behaviourOptions.eventInterval) {
		// 几条数据上报一次，默认实时上传
		window._behaviourOptions.eventInterval = 0
	}

	if (window._behaviourOptions.handleErrorEvent) {
		addHandler(window, 'error', function(message, source, lineno, colno) {
			const errorMsg =
				`${message}${source ? ' [' + source.toString() + ']' : ''}${lineno ? '，行：'+ lineno : ''}${colno ? '，列：'+ colno : ''}`
			pushScriptInfo('error', errorMsg)
		})

		addHandler(window, "unhandledrejection", function() {
			pushScriptInfo('warn', 'unhandledrejection')
		})
	}

	if (isVueEnv) {
		if (window._behaviourOptions.handleErrorEvent) {
			// 全局Vue报错捕获
			VM.config.errorHandler = function(err, vm, info) {
				const errorMsg = `${err ? err : ''}${info ? ' '+ info : ''}`
				pushScriptInfo('error', errorMsg)
			}

			// 全局Vue警告捕获，只适用于开发环境
			VM.config.warnHandler = function(msg, vm, trace) {
				const warnMsg = `${msg ? msg : ''}${trace ? ' '+ trace : ''}`
				pushScriptInfo('warn', warnMsg)
			}
		}
	} else {
		// 非Vue环境，开启全局点击事件捕获
		if (window._behaviourOptions.handleClickEvent) {
			addHandler(window, isMobile ? 'touchstart' : 'click', function(e) {
				const target = e.target
				if (target && target.attributes['yj-behaviour']) {
					const value = target.getAttribute('yj-behaviour')
					if (value) {
						pushClickEvent('', value)
					}
				}
			})
		}
	}
}

export default {
	init,
	pushPageView,
	pushClickEvent
}



// WEBPACK FOOTER //
// ./src/behaviour.js